import React, { useRef, useState } from 'react';
import { Upload, Download, Trash2, FileText, Image as ImageIcon, Presentation, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { capUploadDoc, capDeleteDoc, capDownload } from '../../lib/capstoneApi';
import { inputCls, btnAmber, fmtDate } from './shared';

const DOC_TYPES = ['Charter', 'Data file', 'Report', 'Chart', 'Presentation', 'Other'];

const TypeIcon = ({ d }) => d.is_image ? <ImageIcon className="w-4 h-4 text-sky-300" /> :
  d.type === 'Presentation' ? <Presentation className="w-4 h-4 text-amber-300" /> : <FileText className="w-4 h-4 text-white/50" />;

export default function DocsTab({ project, docs, reloadDocs }) {
  const [docType, setDocType] = useState('Other');
  const [phaseId, setPhaseId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const phases = project.milestones || [];
  const phaseName = (pid) => phases.find((m) => m.id === pid)?.name;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await capUploadDoc(project.id, file, docType, phaseId || null);
      reloadDocs();
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed (8MB limit)');
    } finally {
      setUploading(false);
    }
  };

  const groups = {};
  docs.forEach((d) => {
    const key = d.phase_id && phaseName(d.phase_id) ? phaseName(d.phase_id) : 'General';
    (groups[key] = groups[key] || []).push(d);
  });

  return (
    <div data-testid="cap-docs-tab">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
        className={`rounded-2xl border-2 border-dashed p-7 text-center mb-7 transition-colors ${dragOver ? 'border-amber-300 bg-amber-300/5' : 'border-white/15'}`}
        data-testid="cap-doc-dropzone"
      >
        <Upload className="w-6 h-6 text-white/30 mx-auto mb-2.5" />
        <div className="text-[13px] text-white/60 mb-4">Drag & drop a file here, or choose one — versions are kept automatically (8MB max)</div>
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <select className={`${inputCls} !w-auto`} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="cap-doc-type-select">
            {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select className={`${inputCls} !w-auto`} value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            <option value="">No phase</option>
            {phases.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button className={btnAmber} disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="cap-doc-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload file
          </button>
          <input ref={inputRef} type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
        </div>
      </div>

      {docs.length === 0 && <div className="text-center text-[13px] text-white/35 italic py-8">No documents yet — charters, datasets, charts and reports all live here.</div>}

      {Object.entries(groups).map(([group, items]) => (
        <div key={group} className="mb-7">
          <div className="text-[10px] tracking-[0.22em] font-bold uppercase text-white/45 mb-3">{group}</div>
          <div className="space-y-2">
            {items.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3" data-testid={`cap-doc-row-${d.id}`}>
                <TypeIcon d={d} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white/85 font-medium truncate">{d.name} <span className="text-white/30 font-normal">v{d.version}</span></div>
                  <div className="text-[10.5px] text-white/35">{d.type} · {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by} · {fmtDate(d.uploaded_at)}</div>
                </div>
                <button onClick={() => capDownload(`/capstone/documents/${d.id}/download`, d.name).catch(() => toast.error('Download failed'))}
                  className="p-2 text-white/45 hover:text-amber-300" data-testid={`cap-doc-download-${d.id}`}>
                  <Download className="w-4 h-4" />
                </button>
                {['lead', 'member', 'admin'].includes(project.my_role) && (
                  <button onClick={() => capDeleteDoc(d.id).then(reloadDocs)} className="p-2 text-white/25 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
